import { useEffect } from "react";

const useCaptcha = ({
    setIsVerified,
    captchaName,
}: {
    setIsVerified: (value: boolean) => void;
    captchaName: string;
}) => {
    useEffect(() => {
        const removeCaptchaScripts = () => {
            const script1 = document.querySelector("#mtcaptcha-script");
            if (script1) {
                document.body.removeChild(script1);
            }

            const script2 = document.querySelector("#mtcaptcha-script2");
            if (script2) {
                document.body.removeChild(script2);
            }

            // @ts-expect-error
            window.mtcaptcha = undefined;
            // @ts-expect-error
            window.onCaptchaSuccess = undefined;
        };

        // Define o callback ANTES de carregar os scripts
        // @ts-expect-error
        window.onCaptchaSuccess = function () {
            console.log('onCaptchaSuccess chamado para:', captchaName);
            // @ts-expect-error
            const mtcaptcha = window?.mtcaptcha;
            if (mtcaptcha) {
                try {
                    const status = (mtcaptcha as any).getStatus?.(captchaName);
                    const token = (mtcaptcha as any).getVerifiedToken?.(captchaName);
                    if (status?.isVerified && token) {
                        console.log('Captcha verificado, atualizando estado');
                        setIsVerified(true);
                    } else {
                        console.warn('Captcha não está completamente verificado:', { isVerified: status?.isVerified, hasToken: !!token });
                        setIsVerified(false);
                    }
                } catch (error) {
                    console.error('Erro ao verificar status do captcha:', error);
                    setIsVerified(false);
                }
            } else {
                console.error('mtcaptcha não está disponível no callback');
                setIsVerified(false);
            }
        };

        if (document.querySelector("#mtcaptcha-script") && document.querySelector("#mtcaptcha-script2")) {
            removeCaptchaScripts();
        };
        
        // @ts-expect-error
        window.mtcaptchaConfig = {
            sitekey: "MTPublic-ZhYaX15p1",
            'verified-callback': "onCaptchaSuccess",
            renderQueue: [captchaName], // Usa apenas o captchaName que está sendo usado
            autoFormValidate: false,
            render: 'explicit',
            lang: 'pt',
        };

        const script1 = document.createElement('script');
        script1.id = 'mtcaptcha-script';
        script1.async = true;
        script1.src = 'https://service.mtcaptcha.com/mtcv1/client/mtcaptcha.min.js';
        
        const script2 = document.createElement('script');
        script2.id = 'mtcaptcha-script2';
        script2.async = true;
        script2.src = 'https://service2.mtcaptcha.com/mtcv1/client/mtcaptcha2.min.js';

        // Aguarda o carregamento dos scripts antes de renderizar
        let script1Loaded = false;
        let script2Loaded = false;
        let renderTimeout: NodeJS.Timeout | null = null;

        const handleScriptsLoaded = () => {
            const tryRender = () => {
                const captchaElement = document.getElementById(captchaName);
                // @ts-expect-error
                const mtcaptcha = window?.mtcaptcha;
                // @ts-expect-error
                const mtcaptchaConfig = window?.mtcaptchaConfig;

                if (!captchaElement) {
                    console.warn(`Elemento do captcha (${captchaName}) não encontrado no DOM`);
                    return;
                }

                if (!mtcaptcha) {
                    console.warn('mtcaptcha ainda não está disponível');
                    return;
                }

                if (!mtcaptchaConfig) {
                    console.error('mtcaptchaConfig não está definido');
                    return;
                }

                try {
                    const status = (mtcaptcha as any).getStatus?.(captchaName);
                    if (status && status.isRendered) {
                        if (status.isVerified) {
                            const token = (mtcaptcha as any).getVerifiedToken?.(captchaName);
                            if (token) {
                                console.log('Captcha já está verificado, atualizando estado');
                                setIsVerified(true);
                            }
                        }
                        return;
                    }
                } catch (error) {
                    console.error('Erro ao verificar status do mtcaptcha:', error);
                }
            };

            renderTimeout = setTimeout(tryRender, 500);
        };

        script1.onload = () => {
            script1Loaded = true;
            if (script1Loaded && script2Loaded) {
                handleScriptsLoaded();
            }
        };

        script1.onerror = () => {
            console.error('Erro ao carregar script1 do mtcaptcha');
        };

        script2.onload = () => {
            script2Loaded = true;
            if (script1Loaded && script2Loaded) {
                handleScriptsLoaded();
            }
        };

        script2.onerror = () => {
            console.error('Erro ao carregar script2 do mtcaptcha');
        };

        document.body.appendChild(script1);
        document.body.appendChild(script2);

        return () => {
            // Limpa o timeout se ainda estiver pendente
            if (renderTimeout) {
                clearTimeout(renderTimeout);
            }
            removeCaptchaScripts();
        };
    }, [captchaName, setIsVerified]);
};

export default useCaptcha;